import {ToolRendererEnum} from "../ToolRendererEnum.js"
import {DEFAULT_ITEM_RATING_SCALE_MAX} from "../../SharedConstants.js"
export {DEFAULT_ITEM_RATING_SCALE_MAX} from "../../SharedConstants.js"

const AVERAGE_ROUNDING_FACTOR = 10

function cloneConfig(value) { return JSON.parse(JSON.stringify(value || {})) }
function isMissingValue(value) { return value === "" || value == null }

export function isItemRatingDefinition(definition = {}) {
	return definition?.configRenderer === ToolRendererEnum.PROFILE_DIMENSION_CONFIG
}

function isOrderedDefinition(definition = {}) {
	return definition?.resultRenderer === ToolRendererEnum.CARD_ORDERED_RESULT || definition?.resultRenderer === ToolRendererEnum.CARD_SELECTED_RESULT
}

function validationErrors(validate) {
	let errors = []
	try { validate() } catch (error) { errors = [error.message] }
	return errors
}

export function getItemListConfigErrors(config = {}, definition = {}) {
	return validationErrors(() => {
		if (isItemRatingDefinition(definition)) throwIfInvalidProfileConfig(config)
		else if (isOrderedDefinition(definition)) throwIfInvalidOrderedItemListConfig(config)
		else throwIfInvalidGroupedItemListConfig(config)
	})
}

export function getItemListPayloadErrors(payload = {}, config = {}, definition = {}) {
	const instance = {config}
	return validationErrors(() => {
		if (isItemRatingDefinition(definition)) throwIfInvalidProfileEnginePayload(payload, instance)
		else if (isOrderedDefinition(definition)) throwIfInvalidOrderedItemListEnginePayload(payload)
		else throwIfInvalidGroupedItemListEnginePayload(payload, instance)
	})
}

export function createItemListSummaryInput(payload = {}, definition = {}) {
	return isItemRatingDefinition(definition) ? createProfileSummary(payload) : createItemListSummary(payload)
}

function throwIfInvalidProfileConfig(config = {}) {
	let areas = config.items || config.areas || []
	if (!config.title || !Array.isArray(areas) || (!areas.length && !config.ratingProfile)) {
		throw new Error("Invalid profile tool configuration")
	}
	if (!Number(config.scaleMax) || Number(config.scaleMax) <= 0) {
		throw new Error("Invalid profile scale configuration")
	}
	if (config.ratingProfile) {
		if (![0, 1].includes(config.scaleMin) || !Number.isInteger(Number(config.scaleMax)) || Number(config.scaleMax) < 3 || Number(config.scaleMax) > 10) throw new Error("Invalid rating profile scale")
		if (!config.ratingProfile.question?.trim() || !config.ratingProfile.lowAnchor?.trim() || !config.ratingProfile.highAnchor?.trim()) throw new Error("Rating profile wording is missing")
		if (!["none", "sum"].includes(config.ratingProfile.scoringMethod)) throw new Error("Invalid rating profile scoring method")
	}
	const ids = new Set()
	for (let area of areas) {
		if (!area.id || !area.title) {
			throw new Error("Invalid profile area configuration")
		}
		if (config.ratingProfile && ids.has(area.id)) throw new Error("Duplicate profile area")
		ids.add(area.id)
	}
}

function throwIfInvalidOrderedItemListConfig(config = {}) {
	let items = config.items || []
	if (!config.title || !Array.isArray(items) || !items.length) {
		throw new Error("Invalid ordered item-list tool configuration")
	}
	throwIfInvalidItemListItems(items)
}

function throwIfInvalidGroupedItemListConfig(config = {}) {
	let groups = config.groups || []
	let items = config.items || []
	if (!config.title || !Array.isArray(groups) || !groups.length || !Array.isArray(items) || !items.length) {
		throw new Error("Invalid item-list tool configuration")
	}
	throwIfInvalidItemListGroups(groups)
	throwIfInvalidItemListItems(items)
}

function throwIfInvalidItemListGroups(groups = []) {
	for (let group of groups) {
		if (!group.id || !group.title) {
			throw new Error("Invalid item-list group configuration")
		}
	}
}

function throwIfInvalidItemListItems(items = []) {
	for (let item of items) {
		if (!item.id || !item.title) {
			throw new Error("Invalid item-list item configuration")
		}
	}
}

function throwIfInvalidProfileEnginePayload(enginePayload = {}, toolInstance = null) {
	let profile = enginePayload.itemList || null
	let itemState = enginePayload.itemState || {}
	if (!profile) {
		throw new Error("Invalid profile engine payload")
	}
	throwIfInvalidProfileConfig(profile)
	if (profile.ratingProfile && !(profile.items || profile.areas || []).length) throw new Error("At least one profile area is required")
	throwIfRequiredProfileRatingsMissing(itemState, toolInstance)
	throwIfInvalidProfileRatingValues(itemState, profile)
	if (toolInstance?.config?.ratingProfile && !isValidDateOnly(enginePayload.administrationDate)) throw new Error("Valid administration date is required")
}

function isValidDateOnly(value) {
	if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
	const [year, month, day] = value.split("-").map(Number)
	const date = new Date(Date.UTC(year, month - 1, day))
	return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
}

function throwIfInvalidGroupedItemListEnginePayload(enginePayload = {}, toolInstance = null) {
	let itemList = enginePayload.itemList || null
	let itemState = enginePayload.itemState || {}
	if (!itemList) {
		throw new Error("Invalid item-list engine payload")
	}
	throwIfInvalidGroupedItemListConfig(itemList)
	throwIfRequiredItemListSelectionsMissing(itemState, toolInstance)
	throwIfInvalidItemListSelections(itemState, toolInstance)
}

function throwIfInvalidOrderedItemListEnginePayload(enginePayload = {}) {
	let itemList = enginePayload.itemList || null
	if (!itemList) {
		throw new Error("Invalid ordered item-list engine payload")
	}
	throwIfInvalidOrderedItemListConfig(itemList)
}

function throwIfRequiredItemListSelectionsMissing(itemState = {}, toolInstance = null) {
	for (let item of toolInstance?.config?.items || []) {
		if (isMissingValue(itemState[item.id]?.groupId)) {
			throw new Error("Required item-list selection is missing")
		}
	}
}

function throwIfInvalidItemListSelections(itemState = {}, toolInstance = null) {
	let groupIds = createItemListGroupIdSet(toolInstance?.config?.groups || [])
	for (let item of toolInstance?.config?.items || []) {
		let groupId = itemState[item.id]?.groupId
		if (!isMissingValue(groupId) && !groupIds.has(groupId)) {
			throw new Error("Invalid item-list selection")
		}
	}
}

function throwIfRequiredProfileRatingsMissing(itemState = {}, toolInstance = null) {
	let areas = toolInstance?.config?.items || toolInstance?.config?.areas || []
	for (let area of areas) {
		let rating = itemState[area.id]?.rating
		if (rating == null || rating === "") {
			throw new Error("Required profile rating is missing")
		}
	}
}

function throwIfInvalidProfileRatingValues(itemState = {}, profile = {}) {
	let scaleMax = Number(profile.scaleMax) || DEFAULT_ITEM_RATING_SCALE_MAX
	let scaleMin = profile.ratingProfile && profile.scaleMin === 1 ? 1 : 0
	for (let area of profile.items || profile.areas || []) {
		let rating = Number(itemState[area.id]?.rating)
		if (Number.isNaN(rating) || rating < scaleMin || rating > scaleMax || (profile.ratingProfile && !Number.isInteger(rating))) {
			throw new Error("Invalid profile rating")
		}
	}
}

function createProfileSummary(enginePayload = {}) {
	let itemList = cloneConfig(enginePayload.itemList || {})
	let itemState = cloneConfig(enginePayload.itemState || {})
	let answeredAreas = (itemList.items || itemList.areas || []).filter(area => itemState[area.id]?.rating != null)
	if (itemList.ratingProfile) {
		const areas = itemList.items || itemList.areas || []
		const complete = areas.length > 0 && areas.every(area => Number.isInteger(itemState[area.id]?.rating) && itemState[area.id].rating >= itemList.scaleMin && itemState[area.id].rating <= itemList.scaleMax)
		const lowest = complete ? [...areas].sort((a, b) => itemState[a.id].rating - itemState[b.id].rating)[0] : null
		return {
			headline: lowest ? `${lowest.title}: ${itemState[lowest.id].rating} / ${itemList.scaleMax}` : "Incomplete profile",
			itemList, itemState, administrationDate: enginePayload.administrationDate || "",
			answeredCount: answeredAreas.length,
			configuredSum: calculateConfiguredProfileSum(itemList, itemState),
		}
	}
	return {
		headline: createProfileHeadline(itemList, itemState),
		itemList,
		itemState,
		answeredCount: answeredAreas.length,
		averageScore: calculateProfileAverage(answeredAreas.map(area => ({
			...area,
			rating: itemState[area.id]?.rating,
		}))),
		scaleMax: Number(itemList.scaleMax) || DEFAULT_ITEM_RATING_SCALE_MAX,
	}
}

export function calculateConfiguredProfileSum(config = {}, state = {}) {
	const areas = config.items || config.areas || []
	if (config.ratingProfile?.scoringMethod !== "sum" || !areas.length) return null
	const min = config.scaleMin === 1 ? 1 : 0
	const max = Number(config.scaleMax)
	if (areas.some(area => !Number.isInteger(state[area.id]?.rating) || state[area.id].rating < min || state[area.id].rating > max)) return null
	return areas.reduce((sum, area) => sum + state[area.id].rating, 0)
}

function createItemListSummary(enginePayload = {}) {
	let itemList = cloneConfig(enginePayload.itemList || {})
	let itemState = cloneConfig(enginePayload.itemState || {})
	let progressiveNarrowing = cloneConfig(enginePayload.progressiveNarrowing || {})
	let groupedItems = createItemListGroupedItems(itemList, itemState)
	let orderedItems = createItemListOrderedItems(itemList, itemState)
	let unselectedItems = createItemListUnselectedItems(itemList, itemState)
	return {
		headline: createItemListHeadline(groupedItems, orderedItems),
		itemList,
		itemState,
		progressiveNarrowing,
		groupedItems,
		orderedItems,
		unselectedItems,
		selectedCount: createItemListSelectedCount(itemList, itemState),
	}
}

function createItemListHeadline(groupedItems = [], orderedItems = []) {
	let result = ""
	if (orderedItems.length) {
		result = `${orderedItems[0].title}: #1`
	}
	for (let group of groupedItems) {
		result = getItemListGroupHeadline(result, group)
	}
	return result
}

function getItemListGroupHeadline(currentHeadline = "", group = {}) {
	let result = currentHeadline
	if (!result && group.items?.length) {
		result = `${group.title}: ${group.items.length}`
	}
	return result
}

export function createItemListGroupedItems(itemList = {}, itemState = {}) {
	let items = itemList.items || []
	return (itemList.groups || []).map(group => ({
		...group,
		items: items.filter(item => itemState[item.id]?.groupId === group.id),
	}))
}

export function createItemListOrderedItems(itemList = {}, itemState = {}) {
	return (itemList.items || [])
		.filter(item => itemState[item.id]?.rank != null || itemState[item.id]?.isSelected)
		.sort((a, b) => getItemListItemRank(itemState, a.id) - getItemListItemRank(itemState, b.id))
}

function createItemListUnselectedItems(itemList = {}, itemState = {}) {
	return (itemList.items || []).filter(item => isItemListItemUnselected(itemState[item.id]))
}

function createItemListSelectedCount(itemList = {}, itemState = {}) {
	return (itemList.items || []).filter(item => !isItemListItemUnselected(itemState[item.id])).length
}

function isItemListItemUnselected(itemState = {}) {
	return isMissingValue(itemState?.groupId) && itemState?.rank == null && !itemState?.isSelected
}

export function getItemListItemRank(itemState = {}, itemId = "") {
	return Number(itemState[itemId]?.rank ?? Number.MAX_SAFE_INTEGER)
}

function createItemListGroupIdSet(groups = []) {
	return new Set(groups.map(group => group.id))
}

function createProfileHeadline(profile = {}, itemState = {}) {
	let result = ""
	let strongestArea = null
	for (let area of profile.items || profile.areas || []) {
		let rating = itemState[area.id]?.rating
		if (rating != null && (!strongestArea || Number(rating) > Number(strongestArea.rating))) {
			strongestArea = {
				...area,
				rating,
			}
		}
	}
	if (strongestArea) {
		result = `${strongestArea.title}: ${strongestArea.rating} / ${Number(profile.scaleMax) || DEFAULT_ITEM_RATING_SCALE_MAX}`
	}
	return result
}

export function calculateProfileAverage(areas = []) {
	let result = null
	if (areas.length) {
		result = Math.round(areas.reduce((sum, area) => sum + Number(area.rating || 0), 0) / areas.length * AVERAGE_ROUNDING_FACTOR) / AVERAGE_ROUNDING_FACTOR
	}
	return result
}
